import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Info, Plus, Trash } from "@phosphor-icons/react";
import { defaultExperience, experienceSchema } from "@reactive-resume/schema";
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Tooltip,
} from "@reactive-resume/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";
import { MonthYearDatePicker } from "../sections/shared/month-year-date-picker";
import { MastersUnionListEditor } from "../sections/masters-union-summary";

const formSchema = experienceSchema;

type FormValues = z.infer<typeof formSchema>;

export const ExperienceDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultExperience,
    resolver: zodResolver(formSchema),
  });

  const { isOpen } = useDialog<FormValues>("experience");
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "designations",
  });
  const watchedDesignations = form.watch("designations");

  useEffect(() => {
    if (!isOpen || !isMastersUnion) return;

    const currentDesignations = form.getValues("designations");
    if (Array.isArray(currentDesignations) && currentDesignations.length > 0) return;

    replace([
      {
        title: form.getValues("position") ?? "",
        date: form.getValues("date") ?? "",
      },
    ]);
  }, [form, isMastersUnion, isOpen, replace]);

  useEffect(() => {
    if (!isMastersUnion) return;

    form.setValue("position", watchedDesignations?.[0]?.title ?? "", { shouldValidate: true });
  }, [form, isMastersUnion, watchedDesignations]);

  return (
    <SectionDialog<FormValues> id="experience" form={form} defaultValues={defaultExperience}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="company"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Company`}</FormLabel>
              <FormControl>
                <URLInput
                  {...field}
                  value={
                    typeof field.value === "string"
                      ? { label: field.value, href: "" }
                      : field.value
                  }
                  placeholder="Company Name"
                  labelPlaceholder="Company Website"
                  disableAutoHttps={true}
                  swapLabelAndHref={true}
                  onChange={(value) => {
                    field.onChange(value);
                    form.setValue(
                      "url",
                      {
                        ...(typeof form.getValues("url") === "object" ? form.getValues("url") : {}),
                        href: value.href,
                      },
                      { shouldValidate: true },
                    );
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isMastersUnion && (
          <FormField
            name="companyDescription"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel><span style={{fontSize: '14px'}}>Company Description</span></FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Company description" maxLength={90} />
                </FormControl>
                <p className="text-xs text-muted-foreground">{`Character limit ${(field.value ?? "").length}/90`}</p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {!isMastersUnion && (
          <FormField
            name="position"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t({
                    message: "Position",
                    context: "Position held at a company, for example, Software Engineer",
                  })}
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Position" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          name="location"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Location`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="City, Country" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{isMastersUnion ? "Company Date Range" : t`Date or Date Range`}</FormLabel>
              <FormControl>
                <MonthYearDatePicker value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isMastersUnion && (
          <div className="sm:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <FormLabel>Designations</FormLabel>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-x-2"
                onClick={() => append({ title: "", date: "" })}
              >
                <Plus size={14} />
                <span>Add Designation</span>
              </Button>
            </div>

            {fields.map((designation, index) => (
              <div key={designation.id} className="rounded-md border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    {index === 0 ? "Primary Designation" : `Designation ${index + 1}`}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={fields.length === 1}
                    onClick={() => remove(index)}
                  >
                    <Trash size={14} />
                  </Button>
                </div>

                <FormField
                  name={`designations.${index}.title`}
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="RF Software Engineer" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name={`designations.${index}.date`}
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date or Date Range</FormLabel>
                      <FormControl>
                        <MonthYearDatePicker
                          value={field.value}
                          onChange={field.onChange}
                          disabled={fields.length === 1}
                        />
                      </FormControl>
                      {fields.length === 1 && (
                        <p className="text-xs text-muted-foreground">
                          Add another designation to enable individual date ranges.
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>
        )}

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel className="flex items-center gap-2">
                <span>{t`Summary`}</span>
                {isMastersUnion && (
                  <Tooltip content="Click on “Add More” to add more bullet points">
                    <span className="inline-flex cursor-help text-muted-foreground">
                      <Info size={14} />
                    </span>
                  </Tooltip>
                )}
              </FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <MastersUnionListEditor
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                ) : (
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        content={editor.getHTML()}
                        onChange={(value) => {
                          editor.commands.setContent(value);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
